import React, {useState, useEffect} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import {
  CustomInput,
  CustomSelect,
  CustomToggle,
  CustomTextarea
} from '../Components/CustomFormElements';
import Button from '../Components/Buttons';
import {AlertProvider, useAlert} from '../Components/AlertContext';
import {ConfirmationProvider, useConfirmation} from '../Components/ConfirmationContext';
import PageLoader from '../Components/PageLoader';
import {ConnectionObjectApiProvider, useConnectionObjectApi} from "../Services/ConnectionObjectApiProvider";
import {isEmpty} from '../utils/validationUtil';
import { v4 as UUID } from 'uuid';
import ConnectionObjectSchemaAttributes from './ConnectionObjectSchemaAttributes';

function ConnectionObjectDetail({canEdit}) {
  const navigate = useNavigate();
  const {organizationId, connectionObjectId} = useParams();
  const {showAlert} = useAlert();
  const {showConfirmation} = useConfirmation();

  const {
    createConnectionObject,
    updateConnectionObjectById,
    getConnectionObjectById,
    getConnections,
  } = useConnectionObjectApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isNew, setIsNew] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);

  const [connections, setConnections] = useState([]);

  const [connectionObjectName, setConnectionObjectName] = useState('');
  const [connectionObjectType, setConnectionObjectType] = useState('');
  const [connectionKey, setConnectionKey] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [schemaAttributes, setSchemaAttributes] = useState([]);

  // HTTP Object settings (мінімальні додавання)
  const [httpMethod, setHttpMethod] = useState('POST');
  const [httpEndpoint, setHttpEndpoint] = useState('');

  useEffect(() => {
    const func = async () => {
      setLoading(true);
      getConnections().then(connections => {
        setConnections(connections ?? []);
      });
      try {
        if (connectionObjectId !== 'new-connection-object') {
          const response = await getConnectionObjectById(connectionObjectId);

          setConnectionObjectName(response.name);
          setConnectionObjectType(response.type);
          setConnectionKey(response.connection_key);
          setDescription(response.description || '');
          setIsActive(response.is_active);

          // ⬇️ заміна читання http_object на http_method + endpoint
          if (response.http_method) {
            setHttpMethod(response.http_method || 'POST');
          }
          if (response.endpoint) {
            setHttpEndpoint(response.endpoint || '');
          }

          // схема
          if (response.schema_object && response.schema_object.object_attributes) {
            try {
              const schemaWithKeys = Array.isArray(response.schema_object.object_attributes)
                ? response.schema_object.object_attributes.map(attr => ({
                    key: attr.key || UUID(),
                    ...attr
                  }))
                : [];
              setSchemaAttributes(schemaWithKeys);
            } catch (e) {
              console.error('Error parsing schema:', e);
              setSchemaAttributes([]);
            }
          }
        }
      } catch (error) {
        setError('Error fetching connection object data');
        console.error('Error fetching connection object data:', error);
      } finally {
        setLoading(false);
      }
    }
    func();
  }, [connectionObjectId]);

  useEffect(() => {
    setIsNew(connectionObjectId === 'new-connection-object');
    setIsDisabled(!canEdit);
  }, [connectionObjectId, canEdit])

  const onSave = async () => {
    if (isEmpty(connectionObjectName)) {
      showAlert('fail', 'Connection Object name is required');
      return;
    }
    if (isEmpty(connectionObjectType)) {
      showAlert('fail', 'Connection Object type is required');
      return;
    }
    if (isEmpty(connectionKey)) {
      showAlert('fail', 'Connection is required');
      return;
    }

    setLoading(true);

    try {
      const schemaPayload = {
        object_attributes: schemaAttributes.length > 0
          ? schemaAttributes.map(({ key, ...rest }) => rest)
          : []
      };

      // ⬇️ заміна відправки http_object → на окремі поля http_method / endpoint
      const httpMethodPayload =
        connectionObjectType === 'HTTP_OBJECT' ? httpMethod : null;
      const httpEndpointPayload =
        connectionObjectType === 'HTTP_OBJECT' ? (httpEndpoint || null) : null;

      if (isNew) {
        const createPayload = {
          name: connectionObjectName,
          type: connectionObjectType,
          connection_key: connectionKey,
          description: description || null,
          schema_object: schemaPayload,
          http_method: httpMethodPayload,
          endpoint: httpEndpointPayload,
        };
        await createConnectionObject(createPayload);
        showAlert('success', 'Connection Object created successfully');
      } else {
        const updatePayload = {
          name: connectionObjectName,
          description: description || null,
          schema_object: schemaPayload,
          http_method: httpMethodPayload,
          endpoint: httpEndpointPayload,
        };
        await updateConnectionObjectById(connectionObjectId, updatePayload);
        showAlert('success', 'Connection Object updated successfully');
      }
      navigate(`/organizations/${organizationId}/connection-objects`);
    } catch (error) {
      console.error('Error saving connection object:', error);
      showAlert('fail', 'Failed to save connection object');
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    navigate(`/organizations/${organizationId}/connection-objects`);
  };

  if (loading) {
    return <PageLoader/>;
  }

  return (
    <div className="container">
      <Breadcrumbs/>
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <h2 className='h2-title'>{connectionObjectName || 'New Connection Object'}</h2>
          <div className='gap d-flex flex-row-to-col flex-column'>
            <div className='int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr'>
              <h4 className='h4-title'>Main Settings</h4>

              <div className='input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>Connection Object name</div>
                  <div className='small-font'>Enter connection object name</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomInput
                    id="connection-object-name"
                    label="Connection Object Name"
                    placeholder="Enter connection object name"
                    type="text"
                    value={connectionObjectName}
                    onChange={(id, value) => setConnectionObjectName(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className='input-row input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>Connection Object Type</div>
                  <div className='small-font'>Select connection object type</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomSelect
                    id='connection-object-type'
                    label="Connection Object Type"
                    options={[
                      { value: 'LEAD', label: 'Lead' },
                      { value: 'PROCESS', label: 'Process' },
                      { value: 'FILE', label: 'File' },
                      { value: 'BUYER', label: 'Buyer' },
                      { value: 'CARD', label: 'Card' },
                      { value: 'HTTP_OBJECT', label: 'HTTP Object' },
                    ]}
                    value={connectionObjectType}
                    onChange={(value) => setConnectionObjectType(value)}
                    multiSelect={false}
                    disabled={isDisabled || !isNew}
                  />
                </div>
              </div>

              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Connection</div>
                  <div className="small-font">Select connection</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomSelect
                    id="connection-select"
                    label="Connection"
                    options={(connections || []).map(conn => ({value: conn.id, label: conn.name}))}
                    value={connectionKey}
                    multiSelect={false}
                    onChange={val => setConnectionKey(val)}
                    disabled={isDisabled || !isNew}
                  />
                </div>
              </div>

              {/* ⬇️ HTTP Method окремим блоком */}
              {connectionObjectType === 'HTTP_OBJECT' && (
                <div className='input-row input-block d-flex flex-row justify-between'>
                  <div className='input-description'>
                    <div className='input-title'>HTTP Method</div>
                    <div className='small-font'>Select HTTP method</div>
                  </div>
                  <div className='align-wrapper d-flex align-center'>
                    <CustomSelect
                      id='http-method'
                      label="HTTP Method"
                      options={[
                        { value: 'GET', label: 'GET' },
                        { value: 'POST', label: 'POST' },
                        { value: 'PUT', label: 'PUT' },
                        { value: 'PATCH', label: 'PATCH' },
                        { value: 'DELETE', label: 'DELETE' },
                      ]}
                      value={httpMethod}
                      onChange={(value) => setHttpMethod(value)}
                      multiSelect={false}
                      disabled={isDisabled}
                    />
                  </div>
                </div>
              )}

              {/* ⬇️ Endpoint address окремим блоком */}
              {connectionObjectType === 'HTTP_OBJECT' && (
                <div className='input-row input-block d-flex flex-row justify-between'>
                  <div className='input-description'>
                    <div className='input-title'>Endpoint address</div>
                    <div className='small-font'>Enter HTTP endpoint address</div>
                  </div>
                  <div className='align-wrapper d-flex align-center'>
                    <CustomInput
                      id="http-endpoint"
                      label="Endpoint address"
                      placeholder="Enter endpoint address"
                      type="text"
                      value={httpEndpoint}
                      onChange={(id, value) => setHttpEndpoint(value)}
                      disabled={isDisabled}
                    />
                  </div>
                </div>
              )}

              <div className='input-row input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>Description</div>
                  <div className='small-font'>Enter description (optional)</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomTextarea
                    id="connection-object-description"
                    label="Description"
                    placeholder="Enter description"
                    addedText={description}
                    onChange={(value) => setDescription(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className='input-row input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>Activate connection object</div>
                  <div className='small-font'>Set connection object to be active or inactive</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomToggle
                    id="toggle-status-active"
                    label={isActive ? 'Active' : 'Inactive'}
                    checked={isActive}
                    onChange={(id, value) => setIsActive(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

            </div>

            <ConnectionObjectSchemaAttributes
              schemaAttributes={schemaAttributes}
              setSchemaAttributes={setSchemaAttributes}
              canEdit={canEdit}
              connectionObjectType={connectionObjectType}
            />

            <div className='buttons-container d-flex justify-end'>
              <Button variant='primary' size='medium' disabled={isDisabled} onClick={onSave}>
                Save
              </Button>
              <Button variant='danger' size='medium' onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function ConnectionObjectDetailWrapper({canEdit}) {
  return (
    <AlertProvider>
      <ConfirmationProvider>
        <ConnectionObjectApiProvider>
          <ConnectionObjectDetail canEdit={canEdit}/>
        </ConnectionObjectApiProvider>
      </ConfirmationProvider>
    </AlertProvider>
  );
}

export default ConnectionObjectDetailWrapper;
